import { useState, useEffect, useContext, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Button,
  TextField,
  Autocomplete,
  Card,
  CardContent,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  Backdrop,
  CircularProgress,
  Modal,
  Box,
  IconButton,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  PersonSearch as PersonSearchIcon,
  UploadFile as UploadFileIcon,
  Assessment as AssessmentIcon,
  Work as WorkIcon,
  Folder as FolderIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Dropdown from "./Dropdown";
import { CandidateContext } from "./Context";
import UploadModal from "./Modal";
import { ALERTS, API_URI, END_POINTS } from "./Constants";
import SnackbarComponent from "./common/Snackbar";
import axiosInstance from "../Api/Instance";
import { useSelector } from "react-redux";

function AddProfile() {
  const [isOpen, setIsOpen] = useState(false);
  const [resume, setResume] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [score, setScore] = useState(0);
  const [candidateId, setCandidateId] = useState("");
  const [openLoader, setOpenLoader] = useState(false);
  const [uploadMessageText, setUploadMessageText] = useState("");
  const [isReset, setIsReset] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isQueueModalOpen, setIsQueueModalOpen] = useState(false);
  const [uploadQueueMessage, setUploadQueueMessage] = useState("");
  const navigate = useNavigate();
  const accessToken = useSelector((state) => state.auth.accessToken);

  const {
    isJDChecked,
    applied_position,
    setIsJDChecked,
    setIsMultipleResumeUpload,
    isMultipleResumeUpload,
    errorInResume,
    setErrorInResume,
    snackbarOpen,
    setSnackbarOpen,
  } = useContext(CandidateContext);

  const { GET_CANDIDATES } = END_POINTS;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get(GET_CANDIDATES);
        setCandidates(
          data.candidates.map((c) => ({ label: c.name, id: c._id }))
        );
      } catch (err) {
        console.error("Error fetching candidates:", err.response?.data?.message || err.message);
      }
    })();
  }, [GET_CANDIDATES]);

  const filteredCandidates = useMemo(() => {
    if (!searchTerm) return candidates;
    return candidates.filter((c) =>
      c.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, candidates]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/getCandidateRoles");
        setRoles(data?.roles?.map((r) => r.code) || []);
      } catch (err) {
        console.error("Error fetching roles:", err);
      }
    })();
  }, []);

  const handleJDToggleChange = useCallback(
    (checked) => {
      setIsJDChecked(checked);
      if (checked) {
        setIsMultipleResumeUpload(false);
        setSelectedCandidate(null);
        setResume(null);
        setUploadMessageText("");
      }
    },
    [setIsJDChecked, setIsMultipleResumeUpload]
  );

  const handleFolderUploadToggleChange = useCallback(
    (checked) => {
      setIsMultipleResumeUpload(checked);
      if (checked) {
        setIsJDChecked(false);
        setSelectedCandidate(null);
        setJobDescription("");
      }
    },
    [setIsJDChecked, setIsMultipleResumeUpload]
  );

  const handleResumeChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) {
      setResume(null);
      setUploadMessageText("");
      return;
    }

    if (files.length === 1) {
      setUploadMessageText(files[0].name);
    } else {
      setUploadMessageText(`${files.length} resumes selected`);
    }
    setResume(files);
  }, []);

  const resetFileInput = useCallback(() => {
    setResume(null);
    setUploadMessageText("");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setOpenLoader(true);
    const formData = new FormData();

    try {
      if (selectedCandidate) {
        const { data } = await axiosInstance.post(
          END_POINTS.CANDIDATE_ATS_SCORE,
          { candidate_id: selectedCandidate.id, jobDescription },
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );

        if (isJDChecked) setScore(Math.round(data.atsScore));
        if (data.length === 1) setCandidateId(data[0]._id);

        setIsOpen(true);
        setIsReset(true);
      } else {
        if (isJDChecked && resume?.[0]) {
          formData.append("jobDescription", jobDescription);
          formData.append("resume", resume[0]);
        } else if (resume?.length) {
          resume.forEach((r) => formData.append("resume", r));
          formData.append("applied_position", applied_position);
        }

        const { data } = await axios.post(
          `${API_URI}${END_POINTS.UPLOAD_RESUME}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (isJDChecked) {
          setScore(Math.round(data.atsScore));
          setIsOpen(true);
          setIsReset(true);
        } else {
          setUploadQueueMessage(data.message);
          setIsQueueModalOpen(true);
          setIsReset(true);
          if (data.errors?.length > 0) {
            setErrorInResume(data.errors);
            setSnackbarOpen(true);
          }
        }
      }
    } catch (error) {
      console.error(ALERTS.ERROR_UPLOAD, error);
      setSnackbarOpen(true);
    } finally {
      setOpenLoader(false);
      resetFileInput();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50">
      <div className="px-4 md:px-8 py-6 w-[100%] mx-auto">
        {snackbarOpen && (
          <SnackbarComponent
            open={snackbarOpen}
            handleClose={() => setSnackbarOpen(false)}
            data={errorInResume}
          />
        )}
        {isOpen && (
          <UploadModal
            value={{
              isOpen,
              closeModal: () => setIsOpen(false),
              score,
              candidateId,
              isJDChecked,
              isMultipleResumeUpload,
            }}
          />
        )}
        <Modal open={isQueueModalOpen} onClose={() => setIsQueueModalOpen(false)}>
          <Box sx={{
            position: "absolute", top: "40%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "40%", md: "35%" },
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 28,
            p: 4,
            textAlign: "center",
            outline: "none",
          }}>
            <IconButton
              onClick={() => setIsQueueModalOpen(false)}
              sx={{ position: "absolute", top: "8px", right: "8px" }}
            >
              <CloseIcon />
            </IconButton>

            {/* icon */}
            <Box sx={{
              width: 64, height: 64, borderRadius: "50%",
              background: "linear-gradient(135deg, #3b82f6, #6366f1)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
            }}>
              <span style={{ fontSize: 28 }}>📧</span>
            </Box>

            <Typography variant="h6" fontWeight={700} mb={1}>
              Resumes Queued Successfully!
            </Typography>

            <Typography variant="body1" color="text.secondary" mb={3}>
              {uploadQueueMessage}
            </Typography>

            <Button
              variant="contained"
              onClick={() => {
                setIsQueueModalOpen(false);
                navigate("/candidatelist");
              }}
            >
              Go to Candidate List
            </Button>
          </Box>
        </Modal>

        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={openLoader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
            <Typography
              variant="h4"
              className="font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent"
            >
              Upload & Score Candidates
            </Typography>
          </div>
          <Typography variant="body1" className="text-gray-600 ml-4">
            Upload resumes and analyze candidates with AI-powered ATS scoring
          </Typography>
        </div>

        <form onSubmit={handleSubmit}>
          <Card className="shadow-lg border-0 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <DescriptionIcon className="text-blue-600" />
                  <Typography variant="h6" className="font-semibold text-gray-800">
                    Job Description
                  </Typography>
                </div>

                <div className="flex items-center gap-2">
                  <AssessmentIcon className="text-green-600 text-sm" />
                  <Typography variant="body2" className="text-gray-600 font-medium">
                    ATS Score Analysis:
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isJDChecked}
                        onChange={(e) => handleJDToggleChange(e.target.checked)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                  <Chip
                    label={isJDChecked ? "Enabled" : "Disabled"}
                    size="small"
                    color={isJDChecked ? "success" : "default"}
                    sx={{ fontSize: 12 }}
                  />
                </div>
              </div>

              <TextField
                multiline
                rows={8}
                fullWidth
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste your detailed job description here..."
                variant="outlined"
                required={isJDChecked}
                disabled={!isJDChecked}
              />
            </CardContent>

            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <UploadFileIcon className="text-indigo-600" />
                <Typography variant="h6" className="font-semibold text-gray-800">
                  {isJDChecked ? "Search Candidate or Upload Resume" : "Resume Upload"}
                </Typography>
              </div>

              {isJDChecked && (
                <>
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <PersonSearchIcon className="text-purple-600" />
                      <Typography variant="body1" className="font-medium text-gray-700">
                        Search Existing Candidates
                      </Typography>
                    </div>
                    <Autocomplete
                      disablePortal
                      options={filteredCandidates}
                      value={selectedCandidate}
                      onChange={(_, newValue) => setSelectedCandidate(newValue)}
                      getOptionLabel={(option) => option?.label || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search candidates by name"
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      )}
                    />
                  </div>

                  <div className="flex items-center justify-center my-6">
                    <Divider className="flex-1" />
                    <Typography variant="body2" className="mx-4 text-gray-500 font-medium">
                      OR UPLOAD NEW RESUME
                    </Typography>
                    <Divider className="flex-1" />
                  </div>
                </>
              )}

              {!selectedCandidate && (
                <>
                  <input
                    id="fileUpload"
                    type="file"
                    onChange={handleResumeChange}
                    className="hidden"
                    name="resume"
                    multiple
                    webkitdirectory={isMultipleResumeUpload ? "true" : undefined}
                  />

                  <label
                    htmlFor="fileUpload"
                    className="border-2 border-dashed border-gray-300 rounded-lg p-4 w-full text-center cursor-pointer bg-gray-50 hover:bg-blue-50 transition-all duration-300 flex flex-col items-center gap-2"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                      <CloudUploadIcon className="text-white text-xl" />
                    </div>

                    <Typography variant="body1" className="text-gray-700 font-semibold">
                      {uploadMessageText || "Click to upload resume(s)"}
                    </Typography>
                    <Typography variant="body2" className="text-gray-500 mt-1">
                      {isMultipleResumeUpload
                        ? "Select a folder containing multiple resumes"
                        : "Supports PDF, DOC, DOCX files (Max 10MB)"}
                    </Typography>

                    {resume && (
                      <Chip
                        label={`${resume.length} file(s) selected`}
                        color="success"
                        size="small"
                      />
                    )}
                  </label>
                </>
              )}
            </CardContent>

            <CardContent className="p-6 flex flex-col lg:flex-row justify-between items-center gap-6">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <FolderIcon className="text-orange-600" />
                  <div>
                    <Typography variant="body2" className="font-semibold text-gray-700">
                      Multiple Resumes
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isMultipleResumeUpload}
                          onChange={(e) =>
                            handleFolderUploadToggleChange(e.target.checked)
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label={isMultipleResumeUpload ? "Folder Upload" : "Single File"}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <WorkIcon className="text-blue-600" />
                  <div className="min-w-[200px]">
                    <Typography variant="body2" className="font-semibold text-gray-700">
                      Applied Position
                    </Typography>
                    <Dropdown
                      dropdown="addProfile"
                      isReset={isReset}
                      disabled={isJDChecked}
                      roles={roles}
                      setRoles={setRoles}
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!resume && !selectedCandidate}
                startIcon={isJDChecked ? <AssessmentIcon /> : <CloudUploadIcon />}
                sx={{
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 600,
                  minWidth: 200,
                  height: 48,
                  background: !resume && !selectedCandidate ? "gray" : undefined,
                }}
              >
                {isJDChecked ? "Analyze ATS Score" : "Upload Resume(s)"}
              </Button>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}

export default AddProfile;